<script lang="ts" context="module">

	export const BUY_AGAIN = "buy_again";
	// export const KEY_USER_SPECIFIC_FILTER = "filter";
	export const KEY_USER_SPECIFIC_SORT = "sort";
	export const SAVED_LIST_FILTER_ID = -2;
	const MAX_NUM_FILTERS = 20;

	let _instanceId = 0;

</script>
<script lang="ts">
	import type { AisleLayoutBase, GetBuyAgainItemsResponse, ProductData } from "$lib/def/landing";
	import type { Cache } from "$lib/serv/core/cache";
	import type { streamSelectedStore } from "$lib/serv/groc-landing";

	import { goto } from "$app/navigation";
	import { page } from "$app/stores";
	import { HTTPError } from "$lib/def/api";
	import { GroceryLandingSection, ProductTags } from "$lib/def/landing";
	import { get$, profile$ } from "$lib/serv/core/auth";
	import { fillLayout, getSavedStatusUpdates } from "$lib/serv/groc-product";
	import { LOCATION_PROPS } from "$lib/serv/locations";
	import { manuallyUpdateSavedList } from "$lib/ui/home/groc/swimlanes/SavedItems.svelte";
	import { fromStore } from "$lib/util/obs";
	import { query } from "$lib/util/url";
	import { BehaviorSubject, catchError, combineLatest, delay, distinctUntilChanged, distinctUntilKeyChanged, filter, map, Observable, of, pairwise, shareReplay, startWith, switchMap, tap, withLatestFrom } from "rxjs";
	import { onDestroy } from "svelte";

	import Carousel from "$lib/ui/layout/Carousel.svelte";
	import Dropdown from "$lib/ui/shared/Dropdown.svelte";
	import Header from "$lib/ui/shared/Header.svelte";
	import Product from "./Product.svelte";
	import ProductsBlockLoading from "./ProductsBlockLoading.svelte";

	const instanceId = _instanceId++;

	export let selectedStore$: ReturnType<typeof streamSelectedStore>;

	const createSelectorStream = (queryParamName: string) => {
		return fromStore(page).pipe(
				map((p) => p.url.searchParams.get(queryParamName)),
				distinctUntilChanged(),
				map((value) => {
					const choice = parseInt(value ?? "");
					
					return Number.isNaN(choice) ? 0 : choice;
				}),
			);
	};
	
	/** A stream of the user's filter choices */
	const filter$ = createSelectorStream(BUY_AGAIN);
	/**
	 * Whenever we switch between Buy Again and Saved Items, show the page block loading
	 * instead of just products because the title of the page also changes in this instance
	 */
	const switchAislesSub = filter$
		.pipe(pairwise())
		.subscribe({
			next([oldFilter, newFilter]) {
				if (oldFilter === SAVED_LIST_FILTER_ID || newFilter === SAVED_LIST_FILTER_ID)
					pageLoading = true;
			}
		});

	/** A stream of the user's sort choices */
	const sort$ = createSelectorStream(KEY_USER_SPECIFIC_SORT);

	const retryLoading = new BehaviorSubject(true);

	let pageLoading = true;
	/** An indication to just show block loading in the product section, not the whole page */
	let productsLoading = false;

	const savedItemsUpdates = getSavedStatusUpdates();
	const aisle$ = combineLatest([
		filter$,
		sort$,
		selectedStore$,
		profile$.pipe(delay(0)),
	]).pipe(
		// create a unique hash for the current filter, sort, store and user
		map(([filterId, sortId, store, user]) => ({
			filterId,
			sortId,
			store,
			user,
			hash: `${filterId}-${sortId}-${store?.id}-${user?.id}`,
		})),
		// now reduce noise by only emitting when the hash changes
		distinctUntilKeyChanged('hash'),
		// We only care about saved items list updates if the current filter is "Saved"
		switchMap(params => {
			if (params.filterId === SAVED_LIST_FILTER_ID) {
				return savedItemsUpdates.pipe(
					startWith(null),
					map((savedItemUpdate) => ({
						...params,
						savedItemUpdate,
					})),
				);
			}
			return of({ ...params, savedItemUpdate: null});
		}),
		filter(({store}) => !!store), // TODO: What happens when no store selection?
		tap(() => { productsLoading = true; }),
		// only react to the retry emits now - as late as possible
		switchMap(({filterId, sortId, store, savedItemUpdate}) => retryLoading.pipe(
			switchMap(() => get$<GetBuyAgainItemsResponse>(`ca://grocery/products/buy_again?store_id=${store!.id}&filter=${filterId}&sort=${sortId}`, true)),
			catchError((fetchError) => of(new HTTPError(fetchError))),
			map((response) => ({
				buyAgainInfo: response,
				storeId: store!.id,
			})),
			switchMap(({buyAgainInfo, storeId}) => {
				if (buyAgainInfo instanceof HTTPError) {
					return of(buyAgainInfo);
				}

				const requestObject = {
					type: "???", // not needed here, but platform wants it
					sort: 1, // not needed here, but platform wants it
					// could be "Buy again" or "Frequently bought items"; we only know this after
					// making the buy-again call
					title: buyAgainInfo.title,
					product_selection: {
						catalogue_keys: buyAgainInfo.catalogue_keys,
					},
				};
				return fillLayout([requestObject], storeId).pipe(
					// Sometimes the products that the buy again call returns don't actually exist,
					// then fillLayout throws a 404.  In this case, just send an empty array of
					// products to the template
					catchError((fillError) => of({
						layout_items: [{
							...requestObject,
							products: []
						}],
					} as AisleLayoutBase)),
					map((response) => {
						if (response instanceof HTTPError) return response;
						if (response.layout_items.length === 0) return null;

						const layoutWithProducts = response.layout_items[0];
						layoutWithProducts.products = layoutWithProducts.products ?? [];

						if (filterId === SAVED_LIST_FILTER_ID && savedItemUpdate) {
							layoutWithProducts.products = manuallyUpdateSavedList(layoutWithProducts.products, savedItemUpdate);
							layoutWithProducts.product_selection = {
								catalogue_keys: layoutWithProducts.products.map((p) => p.catalogue_key),
							};
						}

						// Product info response is very lean, so we need to supplement it with the other buy again
						// info that we got from the first call (things like which filters are available etc.)
						return {
							...buyAgainInfo,
							...layoutWithProducts,
						} as GetBuyAgainItemsResponse & { products: ProductData[] };
					}),
				);
			}),
		)),
		tap((response) => {
			pageLoading = false;
			productsLoading = false;
		}),
		shareReplay(1),
	);

	const cache: Observable<Cache<GetBuyAgainItemsResponse & { products: ProductData[]; }>> = aisle$.pipe(
		map((aisle) => ({
			loading: pageLoading || productsLoading,
			...(aisle instanceof HTTPError && { error: aisle }),
			...(!(aisle instanceof HTTPError) && { payload: aisle })
		}) as Cache<GetBuyAgainItemsResponse & { products: ProductData[]; }>),
		// tap(console.log),
		shareReplay(1),
	);

	const subAisle = aisle$.subscribe();
	const subCache = cache.subscribe();
	onDestroy(() => {
		switchAislesSub.unsubscribe();
		subAisle.unsubscribe();
		subCache.unsubscribe();
	});

</script>

<Header back={"/?" + query($page.url).keep(...LOCATION_PROPS, "vertical", "store")} />

{#if pageLoading}
	<h4 class="bold loading display-m">&nbsp;</h4>
	<ProductsBlockLoading rows={2} columns={5} />

{:else if $aisle$}
	{@const aisle = $aisle$}

	{#if aisle instanceof HTTPError}
		<div class="text-center">
			<div class="display-s bold text">Error loading aisle</div>
			<div class="body-m book back grey">We can't process the request right now. Please try again later.</div>
			<div>
				<button class="button" on:click={() => retryLoading.next(true)}>
					<span class="icon-refresh-20"></span>&nbsp;Retry
				</button>
			</div>
		</div>
	{:else}
		{@const chosenFilter = $filter$}
		{@const chosenSort = $sort$}
		{@const cappedFilterOptions = aisle.filter_options.slice(
			0, Math.min(aisle.filter_options.length, MAX_NUM_FILTERS)
		)} <!-- On desktop, we cap how many filters are shown (becomes bulky quickly) -->

		<div class="display-m bold" data-testid="aisle-title">{aisle.title}</div>

		{#if aisle.filter_options.length > 1}
			<div class="pills desktop-only">
			{#each cappedFilterOptions as filterOption}
				{@const active = filterOption.id === chosenFilter}
				<a href={query($page.url).set(BUY_AGAIN, filterOption.id).toUrl()}
					class:active class="label-xl book">
					{#if filterOption.id === SAVED_LIST_FILTER_ID}
						<span class={active ? "icon-heart-filled-20" : "icon-heart-20"}></span>
					{/if}
					{ filterOption.title }
				</a>
			{/each}
			</div>

			<div class="mobile-only">
				<Carousel cache={$cache} type="pills" key={(payload) => payload?.filter_options} let:item={filterOption}>
					{@const active = filterOption.id === chosenFilter}
					<a href={query($page.url).set(BUY_AGAIN, filterOption.id).toUrl()}
						class:active class="label-xl book">
						<!-- setTimeout so that class gets applied first and then repositioning calculation happens after that -->
						{#if filterOption.id === SAVED_LIST_FILTER_ID}
							<span class={active ? "icon-heart-filled-20" : "icon-heart-20"}></span>
						{/if}
						{filterOption.title}
					</a>
				</Carousel>
				<!-- <Carousel {options}>
					Show all subaisles on mobile -->
					<!-- {#each aisle.filter_options as filterOption}
					{/each}
				</Carousel> -->
			</div>
		{/if}

		{#if productsLoading}
			<ProductsBlockLoading rows={2} columns={5} />

		{:else if aisle.products.length > 0}
			<div id="secondaryControls">
				<div class="label-xl medium grey" data-testid="product-count">{aisle.products.length} {aisle.products.length > 1 ? "items" : "item"}</div>

				{#if aisle.sort_options.length > 1}
					<!-- TODO: defaultValue -> we just want the first value, not a whole stream -->
					<div><Dropdown options={aisle.sort_options} name="buyAgainSort" defaultValue={chosenSort} on:selected={({detail: sortOptionId}) => {
							if (sortOptionId === 0)
								goto("/?" + query($page.url).remove(KEY_USER_SPECIFIC_SORT))
							else
								goto("/?" + query($page.url).set(KEY_USER_SPECIFIC_SORT, sortOptionId.toString()))
					}} /></div>
				{/if}

			</div>

			<article class="grid aisle">
				{#each aisle.products as product}
					<Product item={product} hiddenTags={[ProductTags.BuyAgain]} on:product/>
				{/each}
			</article>

		{:else}
		<div class="body-m book back grey text-center">No items currently available</div>

		{/if}
	{/if}
{/if}

<style lang="scss">
	div#secondaryControls {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		margin-bottom: 32px;
	}

	div.pills > a > span[class ^= "icon-heart"] {
		margin-right: 0;
	}
</style>

